import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAuditLogs } from '@/api/audit.ts';
import { useUsers } from '@/api/users/use-users';
import { GenericDataTable } from '@/components/shared/tables';
import { Column } from '@/components/ui/data-table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { RefreshCw, Activity } from 'lucide-react';
import { useSnackbar } from 'notistack';
import { useExportAuditLogs } from '../../api/export/use-export';
import DateTimeFormatter from '@/components/shared/DateTimeFormatter';
import AuditLogDetailsDialog from '@/components/audit/AuditLogDetailsDialog';
import { formatDate } from '@/lib/format';
import { useUrlState } from '@/hooks/shared';
import { PageContainer, PageHeader } from '@/components/shared/layout';
import { EmptyState } from '@/components/shared/EmptyState';

interface AuditLog {
    id: string;
    user_id?: string;
    customer_id?: string;
    customer_name?: string;
    item_type?: string;
    item_id?: string;
    type: string;
    value_before?: string;
    value_after?: string;
    amount?: number;
    currency?: string;
    ip_address?: string;
    user_agent?: string;
    api_key_id?: string;
    metadata?: any;
    created_at: string;
    created_by?: string;
    user?: {
        id: string;
        name: string;
        email: string;
    };
}

const AuditLogPage: React.FC = () => {
    const { enqueueSnackbar } = useSnackbar();

    // Use URL-based state management
    const {
        filters,
        sort,
        pagination,
        updateFilters,
        clearFilters,
        toggleSort,
        goToPage,
        updatePagination,
    } = useUrlState({
        sort: { key: 'created_at', direction: 'desc' },
        pagination: { page: 1, pageSize: 20 },
    });

    // Dialog state
    const [selectedAuditLog, setSelectedAuditLog] = useState<AuditLog | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const { data, isLoading, error, refetch } = useQuery({
        queryKey: ['auditLogs', pagination.page, pagination.pageSize, filters, sort],
        queryFn: () =>
            getAuditLogs({
                page: pagination.page,
                per_page: pagination.pageSize,
                ...filters,
            }),
    });

    // Fetch users for the filter dropdown
    const { data: usersData } = useUsers({ per_page: 100 });
    const users = usersData?.data || [];

    const handleFilterChange = (newFilters: any) => {
        updateFilters(newFilters);
    };

    const handleClearFilters = () => {
        clearFilters();
    };

    const handleSort = (key: keyof AuditLog) => {
        toggleSort(key as string);
    };

    const handlePageSizeChange = (newPageSize: number) => {
        updatePagination({ page: 1, pageSize: newPageSize });
    };

    const handleRefresh = () => {
        refetch();
        enqueueSnackbar('Audit logs refreshed', { variant: 'success' });
    };

    const exportAuditLogsMutation = useExportAuditLogs(
        () => {
            enqueueSnackbar('Audit logs exported successfully', { variant: 'success' });
        },
        (error: Error) => {
            console.error('Export failed:', error);
            enqueueSnackbar('Failed to export audit logs', { variant: 'error' });
        }
    );

    const handleExport = () => {
        exportAuditLogsMutation.mutate({
            format: 'csv',
            filters: { ...filters },
        });
    };

    const handleRowClick = (auditLog: AuditLog) => {
        setSelectedAuditLog(auditLog);
        setIsDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setIsDialogOpen(false);
        setSelectedAuditLog(null);
    };

    // No StatusKind in `@/lib/status` covers audit event types (create/update/delete/auth) —
    // extending that shared map is out of scope here, so this keeps a local mapping but
    // uses semantic tokens instead of palette classes (see docs/CRM_UI_CONVENTIONS.md #3).
    const getEventColor = (event: string) => {
        const eventLower = event.toLowerCase();
        if (eventLower.includes('create') || eventLower.includes('add')) {
            return 'bg-success/10 text-success border-success/20';
        } else if (eventLower.includes('update') || eventLower.includes('edit')) {
            return 'bg-info/10 text-info border-info/20';
        } else if (eventLower.includes('delete') || eventLower.includes('remove')) {
            return 'bg-destructive/10 text-destructive border-destructive/20';
        } else if (eventLower.includes('login') || eventLower.includes('auth')) {
            return 'bg-primary/10 text-primary border-primary/20';
        }
        return 'bg-muted text-muted-foreground border-border';
    };

    // Removed formatDate function - using DateTimeFormatter component instead

    // Define columns for the DataTable
    const columns: Column<AuditLog>[] = [
        {
            id: 'id',
            label: 'ID',
            key: 'id',
            sortable: true,
            width: '120px',
            render: (_, log) => <span className="font-mono text-sm">{log.id.slice(0, 8)}...</span>,
        },
        {
            id: 'user',
            label: 'User',
            key: 'created_by',
            sortable: true,
            width: '200px',
            render: (_, log) => (
                <div>
                    <div className="font-medium">
                        {log.user?.name || log.created_by || 'System'}
                    </div>
                    <div className="text-sm text-muted-foreground">
                        {log.user?.email || log.user_id || 'N/A'}
                    </div>
                </div>
            ),
        },
        {
            id: 'event',
            label: 'Event',
            key: 'type',
            sortable: true,
            width: '150px',
            render: (_, log) => (
                <Badge variant="outline" className={getEventColor(log.type)}>
                    {log.type}
                </Badge>
            ),
        },
        {
            id: 'context',
            label: 'Details',
            key: 'type',
            sortable: false,
            width: '300px',
            render: (_, log) => (
                <div
                    className="max-w-xs truncate"
                    title={log.value_before || log.value_after || 'No details'}
                >
                    {log.value_before || log.value_after || 'No details'}
                </div>
            ),
        },
        {
            id: 'created_at',
            label: 'Timestamp',
            key: 'created_at',
            sortable: true,
            width: '180px',
            render: (_, log) => (
                <div>
                    <div className="font-medium">{formatDate(log.created_at, 'datetime')}</div>
                    <div className="text-sm text-muted-foreground">
                        <DateTimeFormatter date={log.created_at} />
                    </div>
                </div>
            ),
        },
    ];

    // Define filter fields for the GenericDataTable
    const filterFields = useMemo(
        () => [
            {
                key: 'search',
                label: 'Search',
                type: 'text' as const,
                placeholder: 'Search logs...',
            },
            {
                key: 'user_id',
                label: 'User',
                type: 'select' as const,
                options: [
                    { value: 'all', label: 'All users' },
                    ...users.map(user => ({
                        value: user.id,
                        label: user.name,
                    })),
                ],
            },
            {
                key: 'event',
                label: 'Event Type',
                type: 'select' as const,
                options: [
                    { value: 'all', label: 'All events' },
                    { value: 'LEAD_UPDATED', label: 'Lead Updated' },
                    { value: 'LOGIN_SUCCESSFUL', label: 'Login Successful' },
                    { value: 'ORDER_CREATED', label: 'Order Created' },
                    { value: 'ORDER_UPDATED', label: 'Order Updated' },
                    { value: 'ORDER_CANCELLED', label: 'Order Cancelled' },
                    { value: 'ORDER_FILLED', label: 'Order Filled' },
                    { value: 'TRADE_EXECUTED', label: 'Trade Executed' },
                    { value: 'CREATE', label: 'Create' },
                    { value: 'UPDATE', label: 'Update' },
                    { value: 'DELETE', label: 'Delete' },
                    { value: 'LOGIN', label: 'Login' },
                    { value: 'LOGOUT', label: 'Logout' },
                ],
            },
            {
                key: 'date_from',
                label: 'From Date',
                type: 'date' as const,
            },
            {
                key: 'date_to',
                label: 'To Date',
                type: 'date' as const,
            },
        ],
        [users]
    );

    if (error) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                    <Activity className="h-12 w-12 text-destructive mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">Error Loading Audit Logs</h3>
                    <p className="text-muted-foreground mb-4">Failed to load audit logs data.</p>
                    <Button onClick={() => window.location.reload()}>
                        <RefreshCw className="h-4 w-4" />
                        Retry
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <PageContainer fullHeight>
            <PageHeader
                title="Audit Logs"
                description="Monitor system activities and user actions"
                actions={
                    <Button variant="outline" onClick={handleRefresh} disabled={isLoading}>
                        <RefreshCw className="h-4 w-4" />
                        Refresh
                    </Button>
                }
            />

            {/* Generic Data Table */}
            <GenericDataTable<AuditLog>
                data={data?.data || []}
                columns={columns}
                visibleColumns={columns.map(col => col.id)}
                filters={filters}
                filterFields={filterFields}
                presets={[]}
                isLoading={isLoading}
                selectedItems={[]}
                onSelectionChange={() => {}}
                getItemId={log => log.id}
                sortConfig={{
                    key: sort.key as keyof AuditLog,
                    direction: sort.direction,
                }}
                onSort={handleSort}
                totalItems={data?.meta?.total || 0}
                totalPages={data?.meta?.last_page || 1}
                currentPage={pagination.page}
                pageSize={pagination.pageSize}
                onPageChange={goToPage}
                onPageSizeChange={handlePageSizeChange}
                onExport={handleExport}
                onRowClick={handleRowClick}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
                onLoadPreset={() => {}}
                onSavePreset={() => {}}
                onColumnToggle={() => {}}
                onResetColumns={() => {}}
                emptyMessage="No audit logs found"
                loadingMessage="Loading audit logs..."
                stickyLayout
                emptyState={
                    <EmptyState
                        icon={Activity}
                        title="No audit logs found"
                        description="No audit logs match the current filters. Try clearing them."
                        action={
                            <Button variant="outline" size="sm" onClick={handleClearFilters}>
                                Clear filters
                            </Button>
                        }
                    />
                }
            />

            {/* Audit Log Details Dialog */}
            <AuditLogDetailsDialog
                isOpen={isDialogOpen}
                onClose={handleCloseDialog}
                auditLog={selectedAuditLog}
            />
        </PageContainer>
    );
};

export default AuditLogPage;
